import { useEffect, useRef, useState } from "react";

const CATEGORIES = [
  {
    name: "Backers",
    weight: 25,
    color: "#22d3ee",
    icon: "💰",
    tagline: "Who's backing this project?",
    description:
      "Evaluates the quality and track record of investors. Tier 1 backers (e.g. a16z, Paradigm, Sequoia) weigh heavily. Early-stage rounds and recent funding are rewarded more than late-stage or stale raises.",
    signals: [
      "Investor tier classification (Tier 1 / 2 / 3)",
      "Lead investor quality bonus",
      "Funding round stage (Seed scores highest)",
      "Total funding amount (sweet spot: $1M–$5M)",
      "Recency of latest raise (within 3 months = best)",
    ],
  },
  {
    name: "Momentum",
    weight: 25,
    color: "#a78bfa",
    icon: "📈",
    tagline: "Is the project gaining traction?",
    description:
      "Measures real growth across multiple social and on-chain signals over the past 90 days. Follower growth is the only signal that can score negatively — shrinking real audiences drag momentum down rather than floor at zero. Smart-signal thresholds are deliberately strict so only genuinely exceptional growth hits the top of the range.",
    signals: [
      "TwitterScore monthly change (+5/+10/+25/+50 thresholds)",
      "Follower growth (90-day %) — can score negatively: flat band from −1% to +2%, then −0.25 / −0.5 / −0.75 / −1.0 steps at −2% / −5% / −10% / below",
      "Smart follower growth (90-day %) — Moni, +5/+10/+20/+40 thresholds",
      "Smart mentions growth (90-day %) — Moni, +5/+10/+20/+40 thresholds",
      "Sorsa score monthly delta (+5/+20/+50/+100 thresholds)",
    ],
  },
  {
    name: "Social",
    weight: 20,
    color: "#34d399",
    icon: "🌐",
    tagline: "How strong is the social presence?",
    description:
      "Cross-references three independent social scoring providers and averages them. Outliers are automatically dampened so one inflated score can't distort the result.",
    signals: [
      "Sorsa score (formerly TweetScout) — follower quality",
      "Moni score — smart money following + tier",
      "TwitterScore — overall account influence",
      "Outlier dampening (prevents single-source inflation)",
      "Community multiplier (Phase 2 — currently defaults to neutral 1.0x)",
    ],
  },
  {
    name: "Pedigree",
    weight: 20,
    color: "#f59e0b",
    icon: "🏛️",
    tagline: "Who's building this?",
    description:
      "AI-powered founder analysis. Searches the web for the founder's identity, verifies their connection to the project, classifies their background into tiers, and checks their personal social credibility. Strong founders are not penalized for missing social profiles — the tier carries enough signal on its own.",
    signals: [
      "Founder tier (Elite / Proven Crypto / Strong Background / Unverified)",
      "Founder Twitter influence score",
      "Blended formula: max(50% tier + 50% social, 60% tier floor)",
      "AI-verified identity with confidence threshold",
    ],
  },
  {
    name: "Dev",
    weight: 5,
    color: "#60a5fa",
    icon: "⌨️",
    tagline: "Is there active development?",
    description:
      "Checks GitHub activity across the project's top repositories over the past 90 days. Even projects with no public repos get a small baseline — most pre-token crypto projects develop privately. Consistent, modest commit activity is rewarded over sporadic bursts.",
    signals: [
      "Commits per week across top 5 repositories",
      "Formula: 0.35 + 0.65 × sqrt(commits_per_week / 8)",
      "0.35 floor with repos, 0.25 floor without any repos",
    ],
  },
  {
    name: "Revenue",
    weight: 5,
    color: "#f472b6",
    icon: "💎",
    tagline: "Is the protocol generating fees?",
    description:
      "Binary gate: if the protocol generates $1M+ in fees over the past 30 days, it receives full points. Below that threshold, this category scores zero. Most early-stage projects won't have protocol revenue — that's expected.",
    signals: ["30-day protocol fee revenue", "$1M threshold (all-or-nothing)"],
  },
];

const FOUNDER_TIERS = [
  {
    tier: "Tier 1 — Elite",
    score: "1.0",
    description: "FAANG / top protocol founders with proven crypto success",
    color: "#fbbf24",
  },
  {
    tier: "Tier 2 — Proven Crypto",
    score: "0.6",
    description: "Shipped multiple crypto products, raised significant funding, or had exits",
    color: "#34d399",
  },
  {
    tier: "Tier 2.5 — Strong Background",
    score: "0.4",
    description: "Elite non-crypto pedigree (FAANG, finance, PhD) but new to crypto",
    color: "#60a5fa",
  },
  {
    tier: "Tier 3 — Unverified",
    score: "0.2",
    description: "No verifiable professional history or AI couldn't identify the founder",
    color: "#6b7280",
  },
];

const INVESTOR_TIERS = [
  {
    tier: "Tier 1 — Elite",
    examples: "a16z, Paradigm, Sequoia, Polychain, Dragonfly",
    impact: "Highest impact on score",
    color: "#fbbf24",
  },
  {
    tier: "Tier 2 — Established",
    examples: "Prominent VCs, well-known angels",
    impact: "Strong positive signal",
    color: "#34d399",
  },
  {
    tier: "Tier 3 — Emerging",
    examples: "Newer funds, emerging angel investors",
    impact: "Moderate positive signal",
    color: "#60a5fa",
  },
];

function useInView(threshold = 0.12) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          setVisible(true);
          obs.unobserve(el);
        }
      },
      { threshold, rootMargin: "0px 0px -50px 0px" }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, visible] as const;
}

function ScoreDonut({ animate }: { animate: boolean }) {
  const sorted = [...CATEGORIES].sort((a, b) => b.weight - a.weight);
  const circumference = 2 * Math.PI * 70;
  let cumulativeOffset = 0;

  return (
    <div className="meth-donut-container">
      <svg
        viewBox="0 0 200 200"
        className="meth-donut"
        role="img"
        aria-label="Score distribution donut chart showing category weights"
      >
        {sorted.map((cat, i) => {
          const dashLength = (cat.weight / 100) * circumference;
          const dashGap = circumference - dashLength;
          const offset = -(cumulativeOffset / 100) * circumference;
          cumulativeOffset += cat.weight;
          return (
            <circle
              key={cat.name}
              cx="100"
              cy="100"
              r="70"
              fill="none"
              stroke={cat.color}
              strokeWidth="24"
              strokeDasharray={animate ? `${dashLength} ${dashGap}` : `0 ${circumference}`}
              strokeDashoffset={offset}
              strokeLinecap="butt"
              className="meth-donut__segment"
              style={{ transitionDelay: `${i * 150 + 300}ms` }}
            />
          );
        })}
        <text x="100" y="92" textAnchor="middle" className="meth-donut__center-num">
          100
        </text>
        <text x="100" y="114" textAnchor="middle" className="meth-donut__center-label">
          max points
        </text>
      </svg>
      <div className="meth-donut__legend">
        {sorted.map((cat) => (
          <div key={cat.name} className="meth-donut__legend-item">
            <span className="meth-donut__legend-dot" style={{ backgroundColor: cat.color }} />
            <span className="meth-donut__legend-name">{cat.name}</span>
            <span className="meth-donut__legend-weight">{cat.weight}%</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function PipelineStep({
  step,
  title,
  description,
  color,
  isLast,
  index,
}: {
  step: number;
  title: string;
  description: string;
  color: string;
  isLast?: boolean;
  index: number;
}) {
  return (
    <div
      className="meth-pipeline-step meth-stagger"
      style={{ "--reveal-index": index } as React.CSSProperties}
    >
      <div className="meth-pipeline-step__indicator">
        <div className="meth-pipeline-step__dot" style={{ backgroundColor: color, color }}>
          {step}
        </div>
        {!isLast && <div className="meth-pipeline-step__line" />}
      </div>
      <div className="meth-pipeline-step__content">
        <h4 className="meth-pipeline-step__title">{title}</h4>
        <p className="meth-pipeline-step__desc">{description}</p>
      </div>
    </div>
  );
}

function DiscoverySource({
  name,
  icon,
  cadence,
  description,
  color,
  index,
}: {
  name: string;
  icon: string;
  cadence: string;
  description: string;
  color: string;
  index: number;
}) {
  return (
    <div
      className="meth-discovery-source meth-stagger"
      style={{ borderColor: color, "--reveal-index": index } as React.CSSProperties}
    >
      <div className="meth-discovery-source__header">
        <span className="meth-discovery-source__icon">{icon}</span>
        <span className="meth-discovery-source__name">{name}</span>
        <span className="meth-discovery-source__cadence">{cadence}</span>
      </div>
      <p className="meth-discovery-source__desc">{description}</p>
    </div>
  );
}

const EXAMPLE_SCORES = [
  {
    name: "Backers",
    score: 20,
    max: 25,
    color: "#22d3ee",
    note: "Tier 1 lead investor, $3M seed round",
  },
  {
    name: "Momentum",
    score: 18,
    max: 25,
    color: "#a78bfa",
    note: "Strong follower growth, rising smart mentions",
  },
  {
    name: "Social",
    score: 15,
    max: 20,
    color: "#34d399",
    note: "High scores across all 3 providers",
  },
  {
    name: "Pedigree",
    score: 12,
    max: 20,
    color: "#f59e0b",
    note: "Proven Crypto tier founder (Tier 2)",
  },
  { name: "Dev", score: 2.5, max: 5, color: "#60a5fa", note: "Moderate GitHub activity" },
  {
    name: "Revenue",
    score: 0,
    max: 5,
    color: "#f472b6",
    note: "Pre-revenue (typical for early-stage)",
  },
];

const DATA_FLOWS = [
  {
    category: "Backers",
    weight: 25,
    color: "#22d3ee",
    sources: ["RootData", "Discovery"],
    method: "RootData investor + round analysis (discovery data as fallback)",
  },
  {
    category: "Momentum",
    weight: 25,
    color: "#a78bfa",
    sources: ["TwitterScore", "Moni", "Sorsa"],
    method: "Weighted growth velocity composite",
  },
  {
    category: "Social",
    weight: 20,
    color: "#34d399",
    sources: ["Sorsa", "Moni", "TwitterScore"],
    method: "3-provider average with outlier dampening",
  },
  {
    category: "Pedigree",
    weight: 20,
    color: "#f59e0b",
    sources: ["Exa.ai", "OpenAI", "RootData", "TwitterScore", "Moni"],
    method: "AI-powered founder tier classification",
  },
  {
    category: "Dev",
    weight: 5,
    color: "#60a5fa",
    sources: ["GitHub"],
    method: "90-day commit activity (sqrt curve)",
  },
  {
    category: "Revenue",
    weight: 5,
    color: "#f472b6",
    sources: ["DefiLlama Fees"],
    method: "$1M / 30 days binary gate",
  },
];

function ExampleScoreBreakdown({ animate }: { animate: boolean }) {
  const total = EXAMPLE_SCORES.reduce((s, e) => s + e.score, 0);

  return (
    <div className="meth-example">
      <h3 className="meth-example__title">Score Anatomy — Example Project</h3>
      <div className="meth-example__bars">
        {EXAMPLE_SCORES.map((cat, i) => (
          <div key={cat.name} className="meth-example__row">
            <span className="meth-example__name" style={{ color: cat.color }}>
              {cat.name}
            </span>
            <div className="meth-example__track">
              <div
                className={`meth-example__fill${animate ? " meth-example__fill--active" : ""}`}
                style={
                  {
                    "--ex-width": `${(cat.score / cat.max) * 100}%`,
                    "--ex-delay": `${i * 100 + 200}ms`,
                    backgroundColor: cat.color,
                  } as React.CSSProperties
                }
              />
            </div>
            <span className="meth-example__score">
              {cat.score}
              <span className="meth-example__max">/{cat.max}</span>
            </span>
            <span className="meth-example__note">{cat.note}</span>
          </div>
        ))}
      </div>
      <div className="meth-example__total">
        <span className="meth-example__total-label">Alpha Score</span>
        <span className="meth-example__total-value">{total}</span>
        <span className="meth-example__total-max">/ 100</span>
      </div>
    </div>
  );
}

function DataFlowSection() {
  const [ref, visible] = useInView();

  return (
    <div
      className={`meth-section${visible ? " meth-section--visible" : ""}`}
      id="data-flow"
      ref={ref}
    >
      <h2 className="meth-section__title">Data Sources & Flow</h2>
      <p className="meth-section__intro">
        Each category pulls data from specialized APIs. Some providers feed multiple categories —
        for example, Moni data powers Social scoring, Momentum tracking, and Pedigree founder
        verification simultaneously.
      </p>

      <div className="meth-flow-rows">
        {DATA_FLOWS.map((flow) => (
          <div
            key={flow.category}
            className="meth-flow-row"
            style={{ "--flow-color": flow.color } as React.CSSProperties}
          >
            <div className="meth-flow-sources">
              {flow.sources.map((src) => (
                <span key={src} className="meth-flow-source">
                  {src}
                </span>
              ))}
            </div>
            <div className="meth-flow-connector">
              <div className="meth-flow-connector__line" />
              <div className="meth-flow-connector__arrow">→</div>
            </div>
            <div className="meth-flow-target">
              <span className="meth-flow-target__name">{flow.category}</span>
              <span className="meth-flow-target__weight">{flow.weight} pts</span>
            </div>
            <span className="meth-flow-method">{flow.method}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function OverviewSection() {
  const [ref, visible] = useInView();

  return (
    <div
      className={`meth-section${visible ? " meth-section--visible" : ""}`}
      id="overview"
      ref={ref}
    >
      <h2 className="meth-section__title">How the Alpha Score Works</h2>
      <p className="meth-section__intro">
        The <strong>Alpha Score</strong> is a composite rating from 0 to 100 that quantifies a
        crypto project's potential before token launch. It combines six independently scored
        categories — each measuring a different dimension of project quality — into a single number
        you can compare across the leaderboard.
      </p>

      <div className="meth-formula-box">
        <h3>The Formula</h3>
        <div className="meth-formula">
          <span className="meth-formula__term" style={{ color: "#22d3ee" }}>
            Backers
          </span>
          <span className="meth-formula__op">+</span>
          <span className="meth-formula__term" style={{ color: "#a78bfa" }}>
            Momentum
          </span>
          <span className="meth-formula__op">+</span>
          <span className="meth-formula__term" style={{ color: "#34d399" }}>
            Social
          </span>
          <span className="meth-formula__op">+</span>
          <span className="meth-formula__term" style={{ color: "#f59e0b" }}>
            Pedigree
          </span>
          <span className="meth-formula__op">+</span>
          <span className="meth-formula__term" style={{ color: "#60a5fa" }}>
            Dev
          </span>
          <span className="meth-formula__op">+</span>
          <span className="meth-formula__term" style={{ color: "#f472b6" }}>
            Revenue
          </span>
          <span className="meth-formula__op">=</span>
          <span className="meth-formula__result">Alpha Score</span>
        </div>
        <p className="meth-formula__note">
          Each category produces a sub-score (0–1), multiplied by its weight to get category points.
          The Alpha Score is the sum of all category points.
        </p>
      </div>

      <ScoreDonut animate={visible} />

      <ExampleScoreBreakdown animate={visible} />
    </div>
  );
}

function CategoriesSection() {
  const [expandedCategory, setExpandedCategory] = useState<string | null>(null);

  return (
    <div className="meth-section" id="categories">
      <h2 className="meth-section__title">Score Categories</h2>
      <p className="meth-section__intro">
        Click any category for details on what's measured and how it's scored.
      </p>

      <div className="meth-categories-grid">
        {CATEGORIES.map((cat, i) => {
          const isExpanded = expandedCategory === cat.name;
          return (
            <button
              key={cat.name}
              type="button"
              className={`meth-category-card ${isExpanded ? "meth-category-card--expanded" : ""}`}
              style={{ "--cat-color": cat.color, "--reveal-index": i } as React.CSSProperties}
              onClick={() => setExpandedCategory(isExpanded ? null : cat.name)}
            >
              <div className="meth-category-card__header">
                <span className="meth-category-card__icon">{cat.icon}</span>
                <div className="meth-category-card__title-group">
                  <h3 className="meth-category-card__name">{cat.name}</h3>
                  <span className="meth-category-card__weight">{cat.weight} pts</span>
                </div>
                <span className="meth-category-card__chevron">{isExpanded ? "▾" : "▸"}</span>
              </div>
              <p className="meth-category-card__tagline">{cat.tagline}</p>

              <div className="meth-category-card__details-wrap">
                <div className="meth-category-card__details">
                  <div className="meth-category-card__details-inner">
                    <p className="meth-category-card__description">{cat.description}</p>

                    <div className="meth-category-card__signals">
                      <h4>What's Measured</h4>
                      <ul>
                        {cat.signals.map((s) => (
                          <li key={s}>{s}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </button>
          );
        })}
      </div>

      <div className="meth-tier-tables">
        <div className="meth-tier-table">
          <h3>Investor Tiers</h3>
          <p className="meth-tier-table__intro">
            Investors are classified into tiers based on track record. Higher-tier investors have a
            greater impact on the Backers score.
          </p>
          <div className="meth-tier-table__rows">
            {INVESTOR_TIERS.map((t) => (
              <div key={t.tier} className="meth-tier-row">
                <span className="meth-tier-row__dot" style={{ backgroundColor: t.color }} />
                <div className="meth-tier-row__content">
                  <strong>{t.tier}</strong>
                  <span className="meth-tier-row__examples">{t.examples}</span>
                  <span className="meth-tier-row__impact">{t.impact}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="meth-tier-table">
          <h3>Founder Tiers</h3>
          <p className="meth-tier-table__intro">
            Founders are evaluated through AI-powered web research and classified into tiers that
            determine the Pedigree sub-score.
          </p>
          <div className="meth-tier-table__rows">
            {FOUNDER_TIERS.map((t) => (
              <div key={t.tier} className="meth-tier-row">
                <span className="meth-tier-row__dot" style={{ backgroundColor: t.color }} />
                <div className="meth-tier-row__content">
                  <strong>{t.tier}</strong>
                  <span className="meth-tier-row__desc">{t.description}</span>
                  <span className="meth-tier-row__score">Sub-score: {t.score}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="meth-callout">
        <h4>Funding Stage & Recency</h4>
        <p>
          The Backers category also considers <em>when</em> and <em>what stage</em> funding
          occurred. Pre-Seed and Seed rounds score highest. Series A is still treated favorably —
          many ICO public-sale candidates sit there. Series B and later are sharply discounted
          because the implied entry valuation has typically passed the retail-upside window. Total
          raised has a sweet spot in the low millions; very large raises score lower for the same
          reason. Recent raises get a recency bonus that tapers as funding ages.
        </p>
        <div className="meth-callout__grid">
          <div className="meth-callout__item">
            <span className="meth-callout__label">Best Stage</span>
            <span className="meth-callout__value">Seed / Pre-Seed</span>
          </div>
          <div className="meth-callout__item">
            <span className="meth-callout__label">Funding Sweet Spot</span>
            <span className="meth-callout__value">$1M – $5M</span>
          </div>
          <div className="meth-callout__item">
            <span className="meth-callout__label">Peak Recency</span>
            <span className="meth-callout__value">≤ 3 months</span>
          </div>
        </div>
      </div>
    </div>
  );
}

function AuditPipelineSection() {
  const [ref, visible] = useInView();

  return (
    <div className={`meth-section${visible ? " meth-section--visible" : ""}`} id="audit" ref={ref}>
      <h2 className="meth-section__title">Audit Pipeline</h2>
      <p className="meth-section__intro">
        When a project is submitted or re-scored, it goes through a multi-stage audit pipeline.
      </p>

      <div className="meth-pipeline">
        <PipelineStep
          step={1}
          title="Handle Submission"
          description="A Twitter/X handle is submitted and validated. One audit allowed per project per hour."
          color="#34d399"
          index={0}
        />
        <PipelineStep
          step={2}
          title="Twitter Verification"
          description="Handle verified against 7 independent sources (RootData, DefiLlama, CoinGecko, CoinMarketCap, CryptoRank, DexScreener, Uniswap). At least 2 must confirm the match."
          color="#22d3ee"
          index={1}
        />
        <PipelineStep
          step={3}
          title="Raises Data (Sequential)"
          description="Investor and team data extracted first via RootData. Team members are passed to the Pedigree provider to optimize downstream founder lookups."
          color="#a78bfa"
          index={2}
        />
        <PipelineStep
          step={4}
          title="Parallel Provider Execution"
          description="All remaining providers run simultaneously — Social (Sorsa, Moni, TwitterScore), Momentum, Pedigree, GitHub, and DefiLlama."
          color="#f59e0b"
          index={3}
        />
        <PipelineStep
          step={5}
          title="Score Aggregation"
          description="Provider outputs normalized to 0–1 sub-scores, multiplied by category weights, and summed into the final Alpha Score. Social scores undergo outlier dampening."
          color="#f472b6"
          index={4}
        />
        <PipelineStep
          step={6}
          title="Finalization"
          description="Only completed audits update the leaderboard score. Failed providers don't overwrite existing scores — protecting leaderboard stability."
          color="#60a5fa"
          index={5}
        />
        <PipelineStep
          step={7}
          title="Token Detection"
          description="Post-audit check against CoinGecko, CMC, DefiLlama, and RootData. If 2+ sources confirm a live token (95% confidence), the project is auto-archived. Single-source matches flag for manual review only."
          color="#f59e0b"
          isLast
          index={6}
        />
      </div>

      <div className="meth-pipeline-notes">
        <div className="meth-pipeline-note">
          <h4>Outlier Dampening</h4>
          <p>
            When 3+ social providers return scores, any score deviating more than 0.20 from the
            median is pulled 65% toward it. This prevents a single inflated provider from distorting
            the average.
          </p>
        </div>
        <div className="meth-pipeline-note">
          <h4>Score Regression Guards</h4>
          <p>
            If a single-source category (Backers, Pedigree, Dev) fails but had a previous score, the
            old score is preserved rather than dropping to zero.
          </p>
        </div>
        <div className="meth-pipeline-note">
          <h4>Provider Safety</h4>
          <p>
            A global kill switch monitors critical providers (Raises, Pedigree, all Social). If any
            fails 5 consecutive audits, scoring queues are automatically paused to prevent mass
            score corruption. Scoring remains paused until explicitly resumed. &ldquo;Project not
            found&rdquo; responses from RootData or DefiLlama (the data source is reachable, the
            specific project just isn&rsquo;t in the dataset) are treated as neutral — they
            don&rsquo;t count toward the trip threshold so legitimate pre-TGE projects can&rsquo;t
            pause scoring by being obscure.
          </p>
        </div>
      </div>
    </div>
  );
}

function DiscoverySection() {
  const [ref, visible] = useInView();

  return (
    <div
      className={`meth-section${visible ? " meth-section--visible" : ""}`}
      id="discovery"
      ref={ref}
    >
      <h2 className="meth-section__title">Discovery Pipeline</h2>
      <p className="meth-section__intro">
        Beyond manual submissions, the system automatically discovers promising pre-token projects
        from three independent sources.
      </p>

      <div className="meth-discovery-sources">
        <DiscoverySource
          name="RootData Fundraising"
          icon="🏦"
          cadence="Weekly"
          description="Scans current and previous month fundraising rounds for new projects. Investor quality is assessed later during scoring."
          color="#22d3ee"
          index={0}
        />
        <DiscoverySource
          name="Moni Smart Money"
          icon="🧠"
          cadence="Weekly"
          description="Tracks smart money mindshare across 9 crypto categories. Surfaces projects trending among informed wallets."
          color="#a78bfa"
          index={1}
        />
        <DiscoverySource
          name="Exa Neural Search"
          icon="🔬"
          cadence="Weekly"
          description="AI-powered web search for new crypto projects, seed rounds, and stealth launches."
          color="#f59e0b"
          index={2}
        />
      </div>

      <h3 className="meth-subsection-title">Filtering Pipeline</h3>
      <p className="meth-section__intro">
        Each candidate passes through filters to ensure only genuine, pre-token, early-stage
        projects make it through.
      </p>

      <div className="meth-filter-flow">
        <div className="meth-filter-step meth-filter-step--pass">
          <div className="meth-filter-step__icon">1</div>
          <div className="meth-filter-step__content">
            <strong>Established Entity Check</strong>
            <span>
              120+ blocklisted entities filtered (exchanges, miners, public companies).
              Late-stage-only or $200M+ funding without early rounds also excluded.
            </span>
          </div>
        </div>
        <div className="meth-filter-connector" />
        <div className="meth-filter-step meth-filter-step--pass">
          <div className="meth-filter-step__icon">2</div>
          <div className="meth-filter-step__content">
            <strong>Handle Resolution</strong>
            <span>
              Each candidate needs a verified Twitter handle, resolved from source data or
              multi-step lookup chains with plausibility checks.
            </span>
          </div>
        </div>
        <div className="meth-filter-connector" />
        <div className="meth-filter-step meth-filter-step--pass">
          <div className="meth-filter-step__icon">3</div>
          <div className="meth-filter-step__content">
            <strong>Duplicate Detection</strong>
            <span>
              Checked against existing database. Already-scored projects may trigger a re-score if
              new raise data is detected.
            </span>
          </div>
        </div>
        <div className="meth-filter-connector" />
        <div className="meth-filter-step meth-filter-step--reject">
          <div className="meth-filter-step__icon">4</div>
          <div className="meth-filter-step__content">
            <strong>Token Detection Gate</strong>
            <span>
              Projects with live tokens on CoinGecko, CoinMarketCap, or DefiLlama are filtered out —
              we focus exclusively on pre-token alpha.
            </span>
          </div>
        </div>
        <div className="meth-filter-connector" />
        <div className="meth-filter-step meth-filter-step--final">
          <div className="meth-filter-step__icon">✓</div>
          <div className="meth-filter-step__content">
            <strong>Project Imported</strong>
            <span>
              Qualifying projects added to the review queue. Admins can approve (triggers scoring)
              or reject with a v2-taxonomy reason that auto-applies the matching pipeline filter —
              see next section.
            </span>
          </div>
        </div>
      </div>

      <h3 className="meth-subsection-title" style={{ marginTop: "2rem" }}>
        Review Queue Filter Feedback (Phase 2)
      </h3>
      <p className="meth-section__intro">
        Every rejection routes through a reason-specific auto-filter so the same class of project
        doesn't re-surface next week. Filters apply to future candidates only — existing pending
        rows are not touched.
      </p>
      <div className="meth-reason-effects">
        <div className="meth-reason-effect">
          <strong>Already has token</strong>
          <span>Blocklists handle + name variants.</span>
        </div>
        <div className="meth-reason-effect">
          <strong>Memecoin</strong>
          <span>
            Blocklists handle. Separately extracts name tokens into the
            <code>memecoin_keyword_suggestions</code> queue — operator approves per keyword before
            it enters the live filter.
          </span>
        </div>
        <div className="meth-reason-effect">
          <strong>Exchange / tradfi / won't tokenize</strong>
          <span>
            Blocklists handle AND adds the project name to the DB-backed
            <code>discovery_established_entities</code> table (seeded from the old hardcoded ~110-
            entry Set; grows via rejections).
          </span>
        </div>
        <div className="meth-reason-effect">
          <strong>Not crypto / Spam</strong>
          <span>Blocklists handle + name variants.</span>
        </div>
        <div className="meth-reason-effect">
          <strong>Dead / dormant</strong>
          <span>Blocklists handle with a 6-month TTL — dormant projects may revive.</span>
        </div>
        <div className="meth-reason-effect">
          <strong>Duplicate</strong>
          <span>No auto-filter. Requires a manual merge action (pending).</span>
        </div>
        <div className="meth-reason-effect">
          <strong>Other</strong>
          <span>Note-only. No auto-filter.</span>
        </div>
      </div>
    </div>
  );
}

function TgeProximitySection() {
  const [ref, visible] = useInView();

  return (
    <div
      className={`meth-section${visible ? " meth-section--visible" : ""}`}
      id="tge-proximity"
      ref={ref}
    >
      <h2 className="meth-section__title">TGE Proximity</h2>
      <p className="meth-section__intro">
        Detects how close a pre-token project is to launching. Does not affect the Alpha Score.
      </p>

      <div className="meth-callout">
        <h4>Sources & confidence</h4>
        <p>
          Three sources: the project's site, Exa neural search across trusted crypto news, and the
          project's last 20 tweets. Own tweet/site = +30, trusted news = +15. Confirmed sources
          carry over between scans; confidence can drop at most 30% per scan. Badge shows at 50%+.
        </p>
      </div>

      <div className="meth-callout">
        <h4>Cadence</h4>
        <p>
          <strong>Weekly</strong> for projects at confidence ≥50, <strong>biweekly</strong> for the
          25–49 watch tier, <strong>monthly</strong> for the full sweep. Inline rescan also fires
          when an audit's token detection flips a <code>*_verified</code> flag FALSE→TRUE. Operators
          can flag launchpads exempt via <code>tge_proximity_exempt</code> (their own vocabulary
          trips TGE keywords without indicating their own launch).
        </p>
      </div>
    </div>
  );
}

function TokenDetectionSection() {
  const [ref, visible] = useInView();

  return (
    <div
      className={`meth-section${visible ? " meth-section--visible" : ""}`}
      id="token-detection"
      ref={ref}
    >
      <h2 className="meth-section__title">Token Detection & Post-TGE Outcomes</h2>
      <p className="meth-section__intro">
        Confirms whether a project has a live, trading token. Runs after every audit. Drives
        auto-archival and seeds the post-TGE outcomes pipeline.
      </p>

      <div className="meth-callout">
        <h4>Detection</h4>
        <p>
          Queries CoinGecko, CoinMarketCap, DefiLlama, and RootData. Single-source match = 80%
          (manual review). Two sources = 90–95%. <strong>Auto-archive only at 95% + live</strong>{" "}
          (≥2 live sources). RootData contract addresses enable a CoinGecko bridge lookup for tokens
          listed under different names.
        </p>
      </div>

      <div className="meth-callout">
        <h4>Post-TGE outcomes pipeline</h4>
        <p>
          On auto-archive with a <code>gecko_id</code>, a row is seeded into{" "}
          <code>tge_outcomes</code> capturing the pre-TGE score baseline and the token's price /
          market cap at detection. A daily cron then snapshots price/mcap at +7d / +30d / +60d /
          +90d and triggers a full re-audit at +30d / +60d / +90d with{" "}
          <code>trigger_source = 'post_tge_rescore'</code>. Rescore audits do NOT update{" "}
          <code>current_alpha_score</code> — post-TGE performance is a separate validation signal.
        </p>
        <p>
          <strong>Eligibility gate.</strong> A project enters the outcomes pipeline only if there's
          a real pre-TGE baseline. Two failure modes set <code>archive_reason</code> to{" "}
          <code>'token_already_trading'</code> (excluded from the pipeline): EITHER the token was
          first traded &gt;14d before project onboarding, OR there are zero{" "}
          <code>rank_snapshots</code> dated before the token's first trade (project was archived
          before any audit completed).
        </p>
      </div>
    </div>
  );
}

function CategoryTaxonomySection() {
  const [ref, visible] = useInView();

  return (
    <div
      className={`meth-section${visible ? " meth-section--visible" : ""}`}
      id="category-taxonomy"
      ref={ref}
    >
      <h2 className="meth-section__title">Project Categories</h2>
      <p className="meth-section__intro">
        Each project is assigned a granular category (~48 total: DEX, Lending, RWA, AI Agent, …)
        rolled up into 10 display families (DeFi, Infra, AI &amp; DePIN, …). RootData tags are the
        primary signal — the resolver picks the lowest-rank (most specific) match. Auto-assign only
        upgrades to a more specific category and never overwrites an admin-locked one. Tag-less
        projects can be classified into RWA via a narrow description-keyword fallback; for any other
        family, no tags = no auto-assign. Categories are display-only — they have no effect on the
        Alpha Score.
      </p>
    </div>
  );
}

function RankingSection() {
  return (
    <div className="meth-section" id="ranking">
      <h2 className="meth-section__title">Ranking, Freshness & Auto-Rescoring</h2>
      <p className="meth-section__intro">
        Projects are ranked by Alpha Score. Rank and score changes use a{" "}
        <strong>30-day rolling window</strong> — each project's current position is compared against
        its most recent snapshot that is at least 30 days old. The window matches the auto-rescore
        cadence so that almost every project has a meaningful baseline to compare against; a shorter
        window would surface no movement for most projects because their scores haven't changed
        since the last re-audit. The breakouts/breakdowns surface still uses a tighter 7-day window
        to detect rapid movers.
      </p>

      <div className="meth-ranking-compact">
        <div className="meth-ranking-item">
          <span className="meth-ranking-badge meth-ranking-badge--up">
            <span className="meth-ranking-arrow">&#x25B2;</span>5
          </span>
          <span>Moved up 5 positions vs 30 days ago</span>
        </div>
        <div className="meth-ranking-item">
          <span className="meth-ranking-badge meth-ranking-badge--down">
            <span className="meth-ranking-arrow">&#x25BC;</span>3
          </span>
          <span>Dropped 3 positions vs 30 days ago</span>
        </div>
        <div className="meth-ranking-item">
          <span className="meth-ranking-badge meth-ranking-badge--stable">&mdash;</span>
          <span>No change in last 30 days</span>
        </div>
        <div className="meth-ranking-item">
          <span className="meth-ranking-badge meth-ranking-badge--new">NEW</span>
          <span>First scored within past 30 days</span>
        </div>
      </div>

      <div className="meth-callout">
        <h4>Score Freshness</h4>
        <p>
          Each project shows a freshness badge with age and scoring context. Hover to see exactly
          what triggered the last audit (UI submission, discovery, auto-rescore, or raise detection)
          and how fresh the data is.
        </p>
        <div className="meth-callout__grid meth-callout__grid--3col">
          <div className="meth-callout__item">
            <span className="meth-callout__label" style={{ color: "#6ee7b7" }}>
              0–7 days
            </span>
            <span className="meth-callout__value">Current — score data is fresh</span>
          </div>
          <div className="meth-callout__item">
            <span className="meth-callout__label" style={{ color: "#fcd34d" }}>
              8–30 days
            </span>
            <span className="meth-callout__value">Aging — social/momentum may have shifted</span>
          </div>
          <div className="meth-callout__item">
            <span className="meth-callout__label" style={{ color: "#fca5a5" }}>
              30+ days
            </span>
            <span className="meth-callout__value">Stale — rescore recommended</span>
          </div>
        </div>
      </div>

      <div className="meth-callout">
        <h4>Automatic Rescoring Triggers</h4>
        <p>
          Scores stay fresh through multiple automatic rescore mechanisms — not just manual
          submissions.
        </p>
        <div className="meth-callout__grid">
          <div className="meth-callout__item">
            <span className="meth-callout__label">Stale Score</span>
            <span className="meth-callout__value">
              Daily at 3 AM UTC — projects not audited in 30 days
            </span>
          </div>
          <div className="meth-callout__item">
            <span className="meth-callout__label">New Raise Detected</span>
            <span className="meth-callout__value">
              Discovery finds new funding round or 50%+ increase — instant rescore
            </span>
          </div>
          <div className="meth-callout__item">
            <span className="meth-callout__label">Post-TGE Checkpoints</span>
            <span className="meth-callout__value">
              After token launch: rescored at 30d, 60d, and 90d with price tracking
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

function ConfidenceSection() {
  const [ref, visible] = useInView();

  return (
    <div
      className={`meth-section${visible ? " meth-section--visible" : ""}`}
      id="confidence"
      ref={ref}
    >
      <h2 className="meth-section__title">Confidence & Protection</h2>
      <p className="meth-section__intro">
        Each audit produces a confidence score (0–100%) reflecting data completeness. If a provider
        times out or errors, the audit continues with remaining providers but confidence drops 15%
        per failure.
      </p>

      <div className="meth-confidence-scale">
        <div
          className="meth-confidence-level"
          style={{ "--conf-color": "#34d399", "--reveal-index": 0 } as React.CSSProperties}
        >
          <div className="meth-confidence-level__bar" style={{ width: "100%" }} />
          <div className="meth-confidence-level__info">
            <strong>85–100% — High</strong>
            <span>
              Most or all providers succeeded. Score is reliable and fully representative.
            </span>
          </div>
        </div>
        <div
          className="meth-confidence-level"
          style={{ "--conf-color": "#fbbf24", "--reveal-index": 1 } as React.CSSProperties}
        >
          <div className="meth-confidence-level__bar" style={{ width: "70%" }} />
          <div className="meth-confidence-level__info">
            <strong>55–85% — Moderate</strong>
            <span>
              Some providers failed. Score is directionally correct but may shift on re-audit.
            </span>
          </div>
        </div>
        <div
          className="meth-confidence-level"
          style={{ "--conf-color": "#ef4444", "--reveal-index": 2 } as React.CSSProperties}
        >
          <div className="meth-confidence-level__bar" style={{ width: "40%" }} />
          <div className="meth-confidence-level__info">
            <strong>Below 55% — Low</strong>
            <span>Multiple providers failed. Score may be significantly incomplete.</span>
          </div>
        </div>
      </div>

      <div className="meth-callout">
        <h4>Audit Outcomes</h4>
        <p>
          Every audit finishes with one of three statuses, determined by which providers succeeded:
        </p>
        <div className="meth-callout__grid meth-callout__grid--3col">
          <div className="meth-callout__item">
            <span className="meth-callout__label" style={{ color: "#34d399" }}>
              Completed
            </span>
            <span className="meth-callout__value">
              At least one social provider succeeded. Score is promoted to the leaderboard.
            </span>
          </div>
          <div className="meth-callout__item">
            <span className="meth-callout__label" style={{ color: "#fbbf24" }}>
              Partial
            </span>
            <span className="meth-callout__value">
              Some providers succeeded but all three social providers failed. Score is recorded but
              not promoted.
            </span>
          </div>
          <div className="meth-callout__item">
            <span className="meth-callout__label" style={{ color: "#ef4444" }}>
              Failed
            </span>
            <span className="meth-callout__value">
              Zero providers succeeded. Data stored for diagnostics only — the previous reliable
              score is preserved.
            </span>
          </div>
        </div>
        <p>
          Providers fail due to API timeouts, rate limits, HTTP errors, or quota exhaustion. The
          audit continues with whichever providers respond — it never blocks on a single failure.
        </p>
      </div>
    </div>
  );
}

export function MethodologyView() {
  return (
    <div className="meth-container">
      <OverviewSection />
      <DataFlowSection />
      <CategoriesSection />
      <AuditPipelineSection />
      <DiscoverySection />
      <TgeProximitySection />
      <TokenDetectionSection />
      <CategoryTaxonomySection />
      <RankingSection />
      <ConfidenceSection />
      <IntelligencePageSection />
    </div>
  );
}

function IntelligencePageSection() {
  const [ref, visible] = useInView();
  return (
    <div
      className={`meth-section${visible ? " meth-section--visible" : ""}`}
      id="intelligence-page"
      ref={ref}
    >
      <h2 className="meth-section__title">Intelligence Page</h2>
      <p className="meth-section__intro">
        Research surface available to all signed-in users, aggregating the live pre-TGE corpus.
        Pulls from the same tables the rest of the app uses — no separate store.
      </p>

      <div className="meth-callout">
        <h4>What's there</h4>
        <ul className="meth-list">
          <li>
            <strong>Population view</strong> — KPI strip, family × score-band median-raise matrix,
            score-vs-funding scatter with log regression, top-20 backer league, backer
            specialization, ecosystem snapshot, stage mix, stealth watchlist, founder-pedigree
            concentration.
          </li>
          <li>
            <strong>Project view</strong> — comparables panel and headline on every project's detail
            modal: cohort positioning across four lenses (Twins, Same Score, Same Category,
            Co-Investors), within-cohort z-score nearest neighbors, cohort-relative residual
            ("undervalued by ~$Xm vs peers"), 90-day trajectory percentile vs cohort median.
          </li>
          <li>
            <strong>Runway stress list</strong> — projects whose latest round is &gt;12 months old
            and whose score is stable (no &gt;5pt drop in 30d).
          </li>
          <li>CSV export available on tabular blocks.</li>
        </ul>
      </div>
    </div>
  );
}
